/**
 * Day arithmetic in one time zone. The phone checks the log in its own zone;
 * the MCP server checks it in the household's, so "today" and "usually given
 * by 9:00" mean the same wall-clock day and time on both.
 */

export interface Calendar {
  /** Midnight starting the day `n` days after the day `d` is in (`n` may be negative). */
  addDays(d: Date, n: number): Date;
  sameDay(a: Date, b: Date): boolean;
  /** Minutes past midnight on the wall clock: 9:30 am is 570. */
  minuteOfDay(d: Date): number;
  /** The instant the wall clock reads `minutes` past midnight on the day `d` is in. */
  atMinute(d: Date, minutes: number): Date;
  /** Midnight starting the calendar date `year`-`month`-`day` (`month` is 1–12). */
  dateStart(year: number, month: number, day: number): Date;
  /** The calendar date `d` is on, as YYYY-MM-DD: "today" for a birth date's age. */
  isoDate(d: Date): string;
}

/** YYYY-MM-DD (`month` is 1–12). */
const iso = (year: number, month: number, day: number) =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** The runtime's own zone: the phone's, in the browser. */
export const localCalendar: Calendar = {
  addDays: (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n),
  sameDay: (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate(),
  minuteOfDay: (d) => d.getHours() * 60 + d.getMinutes(),
  atMinute: (d, minutes) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, minutes),
  dateStart: (year, month, day) => {
    // `setFullYear`, not the constructor, which reads years 0–99 as 1900–1999.
    const d = new Date(2000, 0, 1);
    d.setFullYear(year, month - 1, day);
    return d;
  },
  isoDate: (d) => iso(d.getFullYear(), d.getMonth() + 1, d.getDate()),
};

export const DAY_MS = 24 * 60 * 60 * 1000;

/** `Date.UTC` (fields may overflow), except that years 0–99 stay themselves. */
function utc(year: number, month: number, day: number, minutes: number, seconds = 0): number {
  const d = new Date(0);
  d.setUTCFullYear(year, month, day);
  d.setUTCMinutes(minutes, seconds);
  return d.getTime();
}

interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** An IANA zone's calendar (e.g. America/Chicago), independent of the runtime's zone. */
export function zoneCalendar(timeZone: string): Calendar {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    era: "short",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  });
  const wall = (t: number): Wall => {
    const w: Record<string, number> = {};
    let bc = false;
    for (const { type, value } of fmt.formatToParts(t)) {
      if (type === "era") bc = value === "BC";
      else if (type !== "literal") w[type] = Number(value);
    }
    // Intl counts 1 BC, 2 BC, …; `Date` counts the same years 0, -1, ….
    if (bc) w.year = 1 - w.year!;
    return w as unknown as Wall;
  };
  /** The wall clock at `t`, read as if it were UTC. */
  const wallAsUtc = (t: number) => {
    const w = wall(t);
    return utc(w.year, w.month - 1, w.day, w.hour * 60 + w.minute, w.second);
  };
  /**
   * The instant the zone's wall clock reads this time (fields may overflow,
   * like `Date.UTC`'s). A time skipped by a daylight-saving jump lands just
   * after the jump; a repeated one takes its first occurrence, as `Date` does.
   */
  const instant = (year: number, month: number, day: number, minutes: number) => {
    const target = utc(year, month, day, minutes);
    // The zone's offsets a day either side cover both sides of any change
    // near the target; each gives a candidate instant.
    const candidates = [target - DAY_MS, target, target + DAY_MS].map(
      (t) => target - (wallAsUtc(t) - t),
    );
    const matches = candidates.filter((t) => wallAsUtc(t) === target);
    return new Date(matches.length ? Math.min(...matches) : Math.max(...candidates));
  };
  return {
    addDays: (d, n) => {
      const w = wall(d.getTime());
      return instant(w.year, w.month - 1, w.day + n, 0);
    },
    sameDay: (a, b) => {
      const x = wall(a.getTime());
      const y = wall(b.getTime());
      return x.year === y.year && x.month === y.month && x.day === y.day;
    },
    minuteOfDay: (d) => {
      const w = wall(d.getTime());
      return w.hour * 60 + w.minute;
    },
    atMinute: (d, minutes) => {
      const w = wall(d.getTime());
      return instant(w.year, w.month - 1, w.day, minutes);
    },
    dateStart: (year, month, day) => instant(year, month - 1, day, 0),
    isoDate: (d) => {
      const w = wall(d.getTime());
      return iso(w.year, w.month, w.day);
    },
  };
}
